/**
 * Flash Messages Hook
 * Provides an easy-to-use interface for showing flash messages
 */

import { useCallback } from 'react';
import { useFlashMessagesContext } from '../contexts/FlashMessagesContext';
import { FlashMessage } from '../types/flashMessages';

export const useFlashMessages = () => {
  const { addMessage, removeMessage, clearAllMessages } = useFlashMessagesContext();

  // Show a generic message
  const showMessage = useCallback((message: Partial<FlashMessage>) => {
    addMessage(message);
  }, [addMessage]);

  // Show success message
  const showSuccess = useCallback((message: string, title?: string) => {
    addMessage({
      type: 'success',
      title,
      message,
    });
  }, [addMessage]);

  // Show error message
  const showError = useCallback((message: string, title?: string) => {
    addMessage({
      type: 'error',
      title,
      message,
      sticky: true, // Errors should be sticky by default
    });
  }, [addMessage]);

  // Show warning message
  const showWarning = useCallback((message: string, title?: string) => {
    addMessage({
      type: 'warning',
      title,
      message,
    });
  }, [addMessage]);

  // Show info message
  const showInfo = useCallback((message: string, title?: string) => {
    addMessage({
      type: 'info',
      title,
      message,
    });
  }, [addMessage]);

  // Show the early access message
  const showEarlyAccessMessage = useCallback(() => {
    showMessage({
      id: 'early-access-info',
      type: 'info',
      title: 'Early Access Program',
      message: 'GekkoVet Copilot is currently in active development as part of our early access program. The AI assistant might make mistakes and responses should be verified with professional judgment. We appreciate all feedback at support@gekkovet.com',
      sticky: true,
      persistent: true,
    });
  }, [showMessage]);

  return {
    showMessage,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showEarlyAccessMessage,
    removeMessage,
    clearAllMessages,
  };
};
